/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {ReactNode, useEffect} from 'react';
import TextField, {StandardTextFieldProps} from '@material-ui/core/TextField';
import {GenericObject} from '@ion/core/src/types/entities';
import theme, {dlsAlt, dlsColor, dlsTypography, dlsZIndex} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';
import {useExtendStyles} from '../../../hooks/useExtendStyles';

export interface TextFieldTypes {
  onMount?: Function;
  label?: string | ReactNode;
  hasError?: boolean;
  errorText?: string | ReactNode;
  fieldLabel?: string | ReactNode;
  required?: boolean;
  placeholder?: string | ReactNode;
  inputProps?: GenericObject;
  defaultValue?: string | number | unknown;
  autoComplete?: 'on' | 'off' | 'username';
  styleOverrides?: (theme: DLSTheme) => GenericObject;
  style?: object;
  readOnlyText?: boolean;
  maxLength?: number;
}

export type Props = TextFieldTypes & StandardTextFieldProps;

const useStyles = (theme) => ({
  root : {
    '&:hover' : {
      backgroundColor : ({disabled}) => disabled ? 'none' : dlsAlt('components', 'textfield', 'hoverBackgroundColor')
    }
  },
  inputRoot : {
    '&:hover' : {
      backgroundColor : dlsAlt('components', 'textfield', 'hoverBackgroundColor')
    },
    '& input' : {
      paddingLeft : 'inherit'
    }
  },
  underline : {
    backgroundColor : dlsColor('white'),
    '&:after'       : {
      borderColor : dlsColor('caBlue')
    },
    '&:before' : {
      borderColor       : `${dlsColor('grey5')} !important`,
      borderBottomWidth : '1px !important'
    }
  },
  underlineError : {
    '&:after' : {
      borderColor : theme.palette.error.main
    }
  },
  floatingLabel : {
    zIndex      : dlsZIndex('textFieldFloatingLabel'),
    '&.focused' : {
      color : dlsColor('caBlue')
    }
  },
  floatingLabelError : {
    zIndex      : dlsZIndex('textFieldFloatingLabelError'),
    '&.focused' : {
      color : `${theme.palette.error.main}`
    }
  },
  disabled : {
    '&' : {
      pointerEvents : 'none !important'
    },
    '&:before' : {
      borderBottomStyle : 'solid !important'
    }
  },
  shrink : {
    transformOrigin : '0px',
    marginLeft      : '3px'
  },
  fieldLabel : {
    color        : dlsColor('grey2'),
    marginBottom : '8px',
    ...dlsTypography('body3')
  },
  requiredAsterisk : {
    color  : dlsColor('aaBlue'),
    margin : '0 2px'
  }
});

/**
 * @TextField
 *
 * Defines the TextField component. This component is reusable and can be custom
 * rendered with props.
 *
 * ```typescript
 *
 *   <TextField label='Name'
 *     hasError
 *     errorText='Please enter a valid Name' />
 *
 * ```
 */
const TextFieldWrapper = ({
  onMount,
  label,
  fieldLabel,
  placeholder,
  errorText,
  inputProps,
  style,
  styleOverrides,
  defaultValue,
  disabled = false,
  InputProps,
  hasError = false,
  required = false,
  readOnlyText = false,
  autoComplete = 'on',
  ...props
}: Props) => {
  const _styleOverrides = style ? () => (style) : null;
  const classes         = useExtendStyles(useStyles, _styleOverrides || styleOverrides)({disabled});

  useEffect(() => {
    onMount?.();
  }, []);

  return (
    <>
      {fieldLabel &&
        <div className={classes.fieldLabel}>
          {fieldLabel}
          {required && <span className={classes.requiredAsterisk}>*</span>}
        </div>}
      <TextField error={hasError}
        className={classes.root}
        disabled={disabled}
        required={required}
        InputProps={{
          classes : {
            root      : classes.inputRoot,
            underline : hasError ? classes.underlineError : classes.underline,
            disabled  : classes.disabled
          },
          ...InputProps
        }}
        InputLabelProps={{
          classes : {
            root : hasError
              ? classes.floatingLabelError
              : classes.floatingLabel,
            focused : 'focused',
            shrink  : classes.shrink
          }
        }}
        inputProps={{placeholder, ...inputProps, readOnly: readOnlyText, autoComplete: autoComplete}}
        label={!placeholder && label}
        defaultValue={defaultValue}
        {...props}
        helperText={hasError ? errorText : props?.helperText} />
    </>
  );
};

export default TextFieldWrapper;
